// apps/client/src/hooks/pipelineTracker/useBrokerMonthlyReport.ts
//
// Broker months: settled and submitted volume per broker per calendar month, the band each month
// reaches, and the loan type breakdown. Read by Broker REM and the Board pack.
//
// Wire types live in remContract.ts (a pure module), so the components that render them can be
// tested without a live Firebase client.

import { useMemo } from 'react';
import { REPORT_CALLABLE } from './reportContract';
import { useTrackerCall } from './reportCache';
import { BROKER_MONTHLY_REPORT_ACTION, type BrokerMonthlyResponse } from './remContract';

export function useBrokerMonthlyReport(
  companyId: string | undefined,
  options: { asAtDate?: string; adviserKey?: string; enabled?: boolean } = {}
) {
  const { asAtDate, adviserKey, enabled = true } = options;
  // Absent fields are omitted rather than sent as undefined: the transport turns undefined into
  // null, which the server's date check refuses.
  const request = useMemo(
    () =>
      companyId && enabled
        ? {
            action: BROKER_MONTHLY_REPORT_ACTION,
            companyId,
            ...(asAtDate ? { asAtDate } : {}),
            ...(adviserKey ? { adviserKey } : {}),
          }
        : null,
    [companyId, asAtDate, adviserKey, enabled]
  );
  // A later request always wins: an answer that arrives after the request changed is dropped, so
  // switching broker or month quickly cannot let a slow earlier answer overwrite the one on screen.
  const { data, loading, error, refetch } = useTrackerCall<BrokerMonthlyResponse>(
    REPORT_CALLABLE,
    request,
    // Surfaced, never swallowed: an empty banding table would read as "nobody settled".
    { label: 'PipelineTracker brokerMonthlyReport', describeError: () => 'Broker months could not be loaded.' }
  );
  const response = request && !error ? data : null;
  return { response, loading, error, reload: refetch };
}
